import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, Music, Volume2, VolumeX, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type SongOrder = Tables<"song_orders">;

interface PlaylistTrack {
  id: string;
  title: string;
  subtitle: string;
  order: SongOrder;
  signedUrl?: string;
}

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
};

const formatTime = (seconds: number) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

interface SongPlaylistProps {
  orders: SongOrder[];
}

const SongPlaylist = ({ orders }: SongPlaylistProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [tracks, setTracks] = useState<PlaylistTrack[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);

  // Build playlist from approved/completed orders that have audio
  useEffect(() => {
    const playable = orders.filter(
      o => ["approved", "completed"].includes(o.status) && o.final_audio_url
    );
    setTracks(
      playable.map(o => ({
        id: o.id,
        title: `${eventTypeLabels[o.event_type] || o.event_type} Song`,
        subtitle: `For ${o.recipient_names}`,
        order: o,
      }))
    );
  }, [orders]);

  const getSignedUrl = useCallback(async (track: PlaylistTrack): Promise<string | null> => {
    if (track.signedUrl) return track.signedUrl;
    const filePath = track.order.final_audio_url!;
    if (filePath.startsWith("http")) return filePath;
    const { data } = await supabase.storage
      .from("customer-photos")
      .createSignedUrl(filePath, 3600);
    if (data?.signedUrl) {
      setTracks(prev =>
        prev.map(t => t.id === track.id ? { ...t, signedUrl: data.signedUrl } : t)
      );
      return data.signedUrl;
    }
    return null;
  }, []);

  const playTrack = useCallback(async (index: number) => {
    if (index < 0 || index >= tracks.length) return;
    setCurrentIndex(index);
    setLoadingUrl(true);
    const url = await getSignedUrl(tracks[index]);
    setLoadingUrl(false);
    if (url && audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [tracks, getSignedUrl]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      if (!audioRef.current.src || audioRef.current.src === window.location.href) {
        playTrack(currentIndex);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  }, [isPlaying, currentIndex, playTrack]);

  const handleEnded = useCallback(() => {
    if (currentIndex < tracks.length - 1) {
      playTrack(currentIndex + 1);
    } else {
      setIsPlaying(false);
    }
  }, [currentIndex, tracks.length, playTrack]);

  if (tracks.length === 0) return null;

  const currentTrack = tracks[currentIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl shadow-soft overflow-hidden mb-8 border border-border"
    >
      <audio
        ref={audioRef}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={handleEnded}
        muted={muted}
      />

      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Music className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-semibold text-foreground">My Songs</h2>
            <p className="text-sm text-muted-foreground">{tracks.length} song{tracks.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
      </div>

      {/* Now Playing Bar */}
      <div className="px-4 pb-4">
        <div className="bg-muted/50 rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost" size="icon"
                onClick={(e) => { e.stopPropagation(); if (currentIndex > 0) playTrack(currentIndex - 1); }}
                disabled={currentIndex === 0}
                className="h-8 w-8"
              >
                <SkipBack className="w-4 h-4" />
              </Button>
              <Button
                variant="default" size="icon"
                onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                disabled={loadingUrl}
                className="h-10 w-10 rounded-full"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </Button>
              <Button
                variant="ghost" size="icon"
                onClick={(e) => { e.stopPropagation(); if (currentIndex < tracks.length - 1) playTrack(currentIndex + 1); }}
                disabled={currentIndex >= tracks.length - 1}
                className="h-8 w-8"
              >
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{currentTrack?.title}</p>
              <p className="text-xs text-muted-foreground truncate">{currentTrack?.subtitle}</p>
            </div>

            <Button
              variant="ghost" size="icon"
              onClick={(e) => { e.stopPropagation(); setMuted(m => !m); }}
              className="h-8 w-8 hidden sm:flex"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-3 mt-3">
            <span className="text-xs text-muted-foreground w-10 text-right">{formatTime(currentTime)}</span>
            <Slider
              value={[duration ? (currentTime / duration) * 100 : 0]}
              max={100}
              step={0.1}
              onValueChange={([val]) => {
                if (audioRef.current && duration) {
                  audioRef.current.currentTime = (val / 100) * duration;
                }
              }}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-10">{formatTime(duration)}</span>
          </div>
        </div>
      </div>

      {/* Playlist */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                {tracks.map((track, i) => (
                  <button
                    key={track.id}
                    onClick={() => playTrack(i)}
                    className={`w-full flex items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50 ${
                      i === currentIndex ? "bg-primary/5" : ""
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === currentIndex && isPlaying
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {i === currentIndex && isPlaying ? (
                        <span className="flex gap-0.5">
                          <span className="w-0.5 h-3 bg-primary-foreground animate-pulse" />
                          <span className="w-0.5 h-2 bg-primary-foreground animate-pulse [animation-delay:150ms]" />
                          <span className="w-0.5 h-3 bg-primary-foreground animate-pulse [animation-delay:300ms]" />
                        </span>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${i === currentIndex ? "text-primary" : "text-foreground"}`}>
                        {track.title}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{track.subtitle}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SongPlaylist;
